import { action, autorun, makeObservable, observable } from 'mobx';

import { getRequest } from '../../services/api';
import { ldClient, resolveFlag } from '../../services/launchDarkly';
import type { ApiResponse } from '../../services/types';
import { getErrorMessage } from '../../utils/errors';
import { devError, devLog } from '../../utils/log';
import type { RootStore } from '../RootStore';
import { Loadable } from '../traits/Loadable';

import { type LandingData } from './types';

export class LandingStore extends Loadable {
  private readonly rootStore: RootStore;

  constructor(rootStore: RootStore) {
    super();
    this.rootStore = rootStore;
    makeObservable(this);

    this.resolveAndUpdateFlags();

    ldClient?.on('ready', () => {
      this.resolveAndUpdateFlags();
    });

    ldClient?.on('change', (changes) => {
      if ('landing_page_v2' in changes) {
        this.resolveAndUpdateFlags();
      }
    });

    autorun(() => {
      if (this.rootStore.authStore.isAuthenticationFlowDone) {
        this.resolveAndUpdateFlags();
        if (!this.rootStore.authStore.authorized && this.landingV2Flag && !this.landingData) {
          void this.fetchLandingData();
        }
      }
    });

    if (this.landingV2Flag && !this.landingData && !this.rootStore.authStore.authorized) {
      void this.fetchLandingData();
    }
  }

  private resolveAndUpdateFlags() {
    const countryCode = this.rootStore.liveGeoStore.rawGeo?.countryCode;
    const isEnabledByDefault = Boolean(countryCode && ['US', 'CA'].includes(countryCode));

    const landingV2FF = resolveFlag('landing_page_v2', isEnabledByDefault);
    this.setLandingV2Flag(landingV2FF);
  }

  @observable
  public landingV2Flag = false;

  @action
  public setLandingV2Flag(flag: boolean) {
    this.landingV2Flag = flag;
  }

  @observable
  public landingData: LandingData | null = null;

  @action
  public setLandingData(data: LandingData) {
    this.landingData = data;
  }

  async fetchLandingData() {
    try {
      if (!this.landingV2Flag) {
        devLog('LandingStore: Landing V2 flag is disabled, skipping fetchLandingData');
        return;
      }

      if (this.loading) {
        devLog('LandingStore: Already loading landing data, skipping fetch');
        return;
      }

      if (this.landingData) {
        devLog('LandingStore: Landing data already exists, skipping fetch');
        return;
      }

      this.setLoading(true);
      const response = await getRequest<ApiResponse<LandingData, 'landingData'>>('landing');
      this.setLandingData(response.data.landingData);
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      devError(`Failed to fetch landing data: ${errorMessage}`);
    } finally {
      this.setLoading(false);
    }
  }
}
